import appConfigJson from '../config/app-config.json';
import type { AppConfig } from '../types/appConfig';
const appConfig = appConfigJson as AppConfig;
// Payments Hub service & types
export interface Money { 
    currency: string; 
    amount: number; 
}

export interface Token { 
    tokenIssuer: string; 
    tokenType: string; 
    tokenValue: string; 
}

export interface AccountHolderInformation { 
    firstName?: string; 
    lastName?: string; 
}

export interface BillingAddress { 
    line1?: string; 
    line2?: string; 
    city?: string; 
    administrativeArea?: string; 
    postalCode?: string; 
    country?: string; 
}

export interface PaymentAccount {
  type: string; 
  tokens: Token[]; 
  accountHolderInformation?: AccountHolderInformation; 
  billingAddress?: BillingAddress;
}

export interface SourceOfFunds { 
    fundingSourceType: string; 
    paymentAccount: PaymentAccount; 
}

export interface AuthorizePayVerifyRequest {
  merchantId?: string; 
  merchantTransactionId: string; 
  requestedAmount?: Money; // accepted by payments hub, but configured to send server-side
  sourceOfFunds: SourceOfFunds;
  memberCardNumber?: string;
  legacyAccountId?: string;
  country?: string;
  locationNumber: string;
}

export interface AuthorizePayVerifyResponse { 
    received: AuthorizePayVerifyRequest; 
    note: string; 
}

export async function verifyPayment(
  req: AuthorizePayVerifyRequest,
  extras?: { memberCardNumber?: string; legacyAccountId?: string; country?: string; location?: string; userId?: string },
  jwtToken?: string
): Promise<AuthorizePayVerifyResponse> {
  const body: AuthorizePayVerifyRequest = extras ? {
    ...req,
    memberCardNumber: extras.memberCardNumber || req.memberCardNumber,
    legacyAccountId: extras.legacyAccountId || req.legacyAccountId,
    country: extras.country || req.country
  } : req;
  const endpoint = appConfig.verifyAndEnrollUrl || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  // Add userId header if provided in extras
  if (extras?.userId) {
    headers['userId'] = extras.userId;
  }
  
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }
  const res = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error('Verify failed ' + res.status + ' ' + text);
  }
  return res.json();
}

// Generates a 22-character alphanumeric ID encoding 128 bits of randomness.
// Can only contain letters (a-z, A-Z) and numbers (0-9), no special characters.
export function generateMerchantTransactionId() {
  const bytes = new Uint8Array(16);
  // Use crypto.getRandomValues for CSPRNG
  crypto.getRandomValues(bytes);
  // Convert to base64
  let b64 = btoa(String.fromCharCode(...Array.from(bytes)));
  // Remove all non-alphanumeric characters (including +, /, =, etc.)
  b64 = b64.replace(/[^a-zA-Z0-9]/g, '');
  return b64;
}
